import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import { Badge } from './ui/index.jsx';
import { useCreditBalance } from '../hooks/queries/useCredits.js';

/**
 * 💳 CreditTracker Component
 * 🔧 OPTIMIZED: Display-only component with renewal date
 * Credits come from AuthContext, renewal date from useCreditBalance
 */
export default function CreditTracker({ show = true, compact = false }) {
    const { user, authType } = useAuth();
    const [animationClass, setAnimationClass] = useState('');
    const [lastUsage, setLastUsage] = useState(null);
    const prevCreditsRef = useRef(user?.credits);

    // Get renewal date from balance API (only for authenticated users)
    const { data: balanceData } = useCreditBalance({
        enabled: authType === 'authenticated' && !!user
    });

    // Track credit changes for animations (display only, no fetching)
    useEffect(() => {
        const prevCredits = prevCreditsRef.current;
        const currentCredits = user?.credits;

        if (prevCredits !== undefined && currentCredits !== undefined && prevCredits !== currentCredits) {
            const creditChange = currentCredits - prevCredits;

            if (creditChange < 0) {
                setLastUsage({
                    amount: Math.abs(creditChange),
                    timestamp: Date.now(),
                    type: 'usage'
                });
                setAnimationClass('credit-used');
                setTimeout(() => setAnimationClass(''), 2000);
            } else if (creditChange > 0) {
                setLastUsage({
                    amount: Math.abs(creditChange),
                    timestamp: Date.now(),
                    type: 'purchase'
                });
                setAnimationClass('credit-added');
                setTimeout(() => setAnimationClass(''), 2000);
            }
        }

        prevCreditsRef.current = currentCredits;
    }, [user?.credits]);

    const realtimeCredits = user?.credits || 0;

    if (!show || authType !== 'authenticated' || !user) {
        return null;
    }

    const architecturesRemaining = Math.floor(realtimeCredits / 8);

    // Format renewal/expiry date from balance data
    const getRenewalInfo = () => {
        const periodEnd = balanceData?.balance?.periodEnd || balanceData?.subscription?.renewalDate;
        const status = balanceData?.balance?.subscriptionStatus || balanceData?.subscription?.status;

        if (!periodEnd) return null;

        const date = new Date(periodEnd);
        const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        // If subscription is canceling, show "Expires", otherwise "Renews"
        const isCanceling = status === 'canceling' || status === 'canceled';
        return {
            label: isCanceling ? 'Expires' : 'Renews',
            date: formatted
        };
    };

    const renewalInfo = getRenewalInfo();

    const getCreditWarningLevel = () => {
        if (realtimeCredits <= 8) return 'critical';
        if (realtimeCredits < 24) return 'warning';
        return 'normal';
    };

    const warningLevel = getCreditWarningLevel();

    // Compact View (used in floating bar)
    if (compact) {
        return (
            <div className={`
                flex items-center gap-3 px-4 py-2 rounded-xl backdrop-blur-md border transition-all duration-300
                ${warningLevel === 'critical'
                    ? 'bg-red-500/10 border-red-500/30 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.2)]'
                    : warningLevel === 'warning'
                        ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400 shadow-[0_0_15px_rgba(245,158,11,0.2)]'
                        : 'bg-white/5 border-white/10 text-white shadow-[0_0_15px_rgba(123,97,255,0.1)] hover:bg-white/10 hover:border-white/20'
                } ${animationClass}
            `}>
                <div className="flex flex-col items-end leading-none">
                    <span className="text-sm font-bold">{realtimeCredits} Credits</span>
                    <span className="text-[10px] opacity-70 uppercase tracking-wider">
                        {architecturesRemaining} Builds left
                        {renewalInfo && <span className="ml-1">• {renewalInfo.label} {renewalInfo.date}</span>}
                    </span>
                </div>
                {lastUsage && (
                    <span className={`text-xs px-1.5 py-0.5 rounded ${lastUsage.type === 'usage' ? 'bg-red-500/50' : 'bg-green-500/50'}`}>
                        {lastUsage.type === 'usage' ? '-' : '+'}{lastUsage.amount}
                    </span>
                )}
            </div>
        );
    }

    // Full Card View (used in dashboard/sidebar)
    return (
        <div className={`
            glass-panel p-5 rounded-xl transition-all duration-300 relative overflow-hidden group
            ${animationClass}
            ${warningLevel === 'critical' ? 'border-red-500/30 shadow-[0_0_20px_rgba(239,68,68,0.2)]' : ''}
        `}>
            {/* Holographic shine effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/5 to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ transform: 'rotate(45deg) translate(-50%, -50%) scale(2)' }} />

            <div className="flex items-center justify-between mb-3 relative z-10">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Balance</span>
                <Badge variant={
                    warningLevel === 'critical' ? 'error' :
                        warningLevel === 'warning' ? 'warning' :
                            'success'
                }>
                    {warningLevel === 'critical' ? 'Low Funds' :
                        warningLevel === 'warning' ? 'Running Low' :
                            'Active'}
                </Badge>
            </div>

            <div className="flex items-end justify-between relative z-10">
                <div>
                    <div className="text-3xl font-bold text-white tracking-tight">
                        {realtimeCredits.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                        ~{architecturesRemaining} architectures
                        {renewalInfo && (
                            <span className="ml-2 text-gray-500">
                                • {renewalInfo.label} {renewalInfo.date}
                            </span>
                        )}
                    </div>
                </div>

                {lastUsage && (
                    <div className={`text-xs font-bold px-2 py-1 rounded-lg ${lastUsage.type === 'usage'
                        ? 'bg-red-500/20 text-red-300'
                        : 'bg-green-500/20 text-green-300'
                        }`}>
                        {lastUsage.type === 'usage' ? '-' : '+'}{lastUsage.amount}
                    </div>
                )}
            </div>

            {warningLevel !== 'normal' && (
                <div className="mt-3 pt-3 border-t border-white/10 relative z-10">
                    <p className="text-xs text-red-300 flex items-center gap-2">
                        <span>⚠️</span>
                        {warningLevel === 'critical'
                            ? 'Top up to continue building'
                            : 'Consider reloading soon'
                        }
                    </p>
                </div>
            )}
        </div>
    );
}

// Keep the animation CSS injection
const styles = `
.credit-tracker.credit-used {
    animation: creditUsed 0.6s ease-out;
}

.credit-tracker.credit-added {
    animation: creditAdded 0.6s ease-out;
}

@keyframes creditUsed {
    0% { transform: scale(1); }
    50% { transform: scale(0.98); }
    100% { transform: scale(1); }
}

@keyframes creditAdded {
    0% { transform: scale(1); }
    50% { transform: scale(1.02); }
    100% { transform: scale(1); }
}
`;

if (typeof document !== 'undefined' && !document.getElementById('credit-tracker-anim-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'credit-tracker-anim-styles';
    styleSheet.textContent = styles;
    document.head.appendChild(styleSheet);
}